<template>
    <div
        class="tw:relative tw:flex tw:flex-col tw:gap-1 tw:w-full tw:border tw:border-slate-100 tw:shadow-xs"
        role="timer"
        :aria-label="label || undefined"
        :aria-live="isExpired ? 'assertive' : 'off'"
    >
        <!-- Fila principal: label + tiempo -->
        <div class="tw:flex tw:z-10 tw:px-4 tw:py-[2px] tw:items-center tw:justify-between tw:gap-2">
            <span
                v-if="label"
                :class="[
                    'tw:font-bold tw:tracking-wider tw:text-gray-950 tw:truncate',
                    labelSizeClass,
                ]"
            >
                {{ label }}
            </span>

            <span
                :class="[
                    'tw:font-mono tw:font-bold tw:tabular-nums tw:shrink-0',
                    isExpired
                        ? 'tw:text-red-600'
                        : urgente
                            ? 'tw:text-orange-500'
                            : 'tw:text-gray-950',
                    sizeClass,
                ]"
                aria-atomic="true"
            >
                {{ isExpired ? expiryLabel : countdown.formatted.value }}
            </span>
        </div>

        <!-- Barra de progreso (opcional) -->
        <div
            v-if="showBar"
            class="tw:absolute tw:left-0 tw:top-0 tw:w-full tw:h-full tw:rounded-sm tw:bg-white tw:overflow-hidden"
            aria-hidden="true"
        >
            <div
                :class="[
                    'tw:h-full tw:rounded-sm tw:transition-all tw:duration-500',
                    isExpired
                        ? 'tw:bg-red-500'
                        : urgente
                            ? 'tw:bg-orange-400'
                            : 'tw:bg-green-400',
                ]"
                :style="{ width: isExpired ? '0%' : `${countdown.percentLeft.value}%` }"
            ></div>
        </div>
    </div>
</template>

<script setup>
    import { computed, watch, onMounted } from 'vue';
    import { useCountdown } from '@/composables/common/useCountdown';

    // ─── Props ────────────────────────────────────────────────────────────────────
    const props = defineProps({
        /**
         * Duración total en milisegundos.
         * Mutuamente exclusivo con `endsAt`.
         * Ejemplo: 15 * 60 * 1000  (15 minutos)
         */
        durationMs: {
            type: Number,
            default: null,
        },
        /**
         * Timestamp (ms) o Date que indica cuándo expira.
         * Si se pasa `endsAt`, tiene prioridad sobre `durationMs`.
         */
        endsAt: {
            type: [Number, Date],
            default: null,
        },
        /**
         * Texto descriptivo que precede al tiempo (ej. "Tiempo restante").
         * Si no se pasa, solo se muestra el tiempo.
         */
        label: {
            type: String,
            default: '',
        },
        /**
         * Texto que se muestra cuando el timer expira.
         * Permite sobreescribir el mensaje por defecto sin acoplarse a i18n interno.
         * El componente emite `expired` igualmente; este prop solo afecta al label visible.
         */
        expiryLabel: {
            type: String,
            default: 'Tiempo agotado',
        },
        /**
         * Si true, muestra la barra de progreso debajo del tiempo.
         */
        showBar: {
            type: Boolean,
            default: true,
        },
        /**
         * Tamaño del texto del tiempo. Valores: 'sm' | 'md' | 'lg'
         */
        size: {
            type: String,
            default: 'md',
            validator: v => ['sm', 'md', 'lg'].includes(v),
        },
        /**
         * Umbral en milisegundos a partir del cual el tiempo se resalta en naranja
         * como indicador de urgencia. Por defecto: 2 minutos.
         */
        urgencyThresholdMs: {
            type: Number,
            default: 2 * 60 * 1000,
        },
        /**
         * Si true, el timer arranca automáticamente al montar el componente.
         * Si false, el consumer debe llamar manualmente a start() via template ref.
         */
        autoStart: {
            type: Boolean,
            default: true,
        },
    });

    // ─── Emits ───────────────────────────────────────────────────────────────────
    const emit = defineEmits([
        /**
         * Se emite UNA VEZ cuando el timer llega a 0.
         * No lleva payload; el consumer reacciona al evento para limpiar estado.
         */
        'expired',
    ]);

    // ─── Composable ───────────────────────────────────────────────────────────────
    const countdown = useCountdown({
        durationMs: props.durationMs ?? undefined,
        endsAt: props.endsAt ?? undefined,
        autoStart: false, // controlamos el arranque en onMounted
    });

    // ─── Derived ─────────────────────────────────────────────────────────────────
    const isExpired = computed(() => countdown.isExpired.value);

    const urgente = computed(() =>
        !isExpired.value && countdown.totalMsLeft.value <= props.urgencyThresholdMs
    );

    const sizeClass = computed(() => {
        switch (props.size) {
            case 'sm': return 'tw:text-sm';
            case 'lg': return 'tw:text-2xl';
            default:   return 'tw:text-base';
        }
    });

    const labelSizeClass = computed(() => {
        switch (props.size) {
            case 'sm': return 'tw:text-xs';
            case 'lg': return 'tw:text-lg';
            default:   return 'tw:text-sm';
        }
    });

    // ─── Ciclo de vida ────────────────────────────────────────────────────────────
    onMounted(() => {
        // Registrar callback de expiración ANTES de arrancar
        countdown.onExpired(() => emit('expired'));

        if (props.autoStart) {
            countdown.start();
        }
    });

    // Reaccionar a cambios de props en tiempo de ejecución (ej. endsAt actualizado)
    watch(
        () => [props.durationMs, props.endsAt],
        () => {
            countdown.reset();
            if (props.autoStart) {
                countdown.start();
            }
        }
    );

    // ─── Expose (para template refs del consumer) ─────────────────────────────────
    defineExpose({
        start:  countdown.start,
        stop:   countdown.stop,
        reset:  countdown.reset,
    });
</script>
